/* Thin fetch wrapper.
 *
 * Same-origin by design: nginx serves this bundle and proxies /api to the
 * FastAPI container, so credentials ride along on the session cookie without
 * any CORS configuration.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });

  if (!resp.ok) {
    // The server returns {error: string} already written for a person to read.
    let message = resp.statusText;
    try {
      const body = await resp.json();
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body; keep statusText */
    }
    throw new ApiError(resp.status, message);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body === undefined ? "{}" : JSON.stringify(body),
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/* Is this failure "you are signed out", or "the server did not answer"?
 *
 * ONLY a 401 is the former. Everything else -- the API not running, a proxy
 * 500, a half-started NAS, DNS -- is the latter, and treating them alike is
 * what produced a sign-in screen with nobody on it: the app asked who was
 * there, got an error, and rendered an empty list of faces with no error and
 * no way forward.
 */
export function isSignedOut(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

/* ── shapes returned by the API ── */

export type Health = { ok: boolean; build: string; mongo: boolean };

export type Profile = {
  loginName: string;
  displayName: string;
  role: "parent" | "child";
  avatar: string;
  color: string | null;
};

export type Me = {
  userId: string;
  familyId: string;
  displayName: string;
  role: "parent" | "child";
  childId: string | null;
  capabilities: Record<string, boolean>;
  tier: number | null;
  moneyDisplayStyle: string | null;
  /** A parent's own recovery address. Null until they set one; children never
   *  have one. */
  email: string | null;
};

export type Balances = {
  bankCents: number;
  walletCents: number;
  holdCents: number;
  totalCents: number;
};

export type Child = {
  id: string;
  displayName: string;
  avatar: string;
  color: string;
  capabilityPreset: string;
  presetLabel: string;
  tier: number;
  moneyDisplayStyle: string;
  /** Whether the jars draw the balance as notes and coins rather than a fill
   *  level. On for every child until a grown-up turns it off. */
  showCashPictures: boolean;
  /** Whether finishing the day's jobs plays a tune on the child's screen. On
   *  until a grown-up turns it off. */
  playJingle: boolean;
  /** Who may move money OUT of the Special jar. Into it is never gated. */
  specialPolicy: "parent_only" | "ask_first" | "anytime";
  capabilities: Record<string, boolean>;
  balances: Balances;
  hasLogin: boolean;
  loginName: string | null;
};

export type GrownUp = {
  id: string;
  displayName: string;
  loginName: string;
  /** The signed-in parent. They cannot remove themselves. */
  isYou: boolean;
  hasSignedIn: boolean;
  /** Removed adults stay on the list, flagged, so they can be let back in. */
  isActive: boolean;
  email: string | null;
};

export type Bucket = {
  id: string;
  key: string;
  name: string;
  kind: "give" | "save" | "spend" | "special";
  icon: string;
  color: string | null;
  percentBp: number | null;
  withdrawable: boolean;
  sortOrder: number;
  balanceCents: number;
  walletCents: number;
  holdCents: number;
  interest: { enabled?: boolean; rate_bp?: number } | null;
};

export type TransferRequest = {
  id: string;
  childId: string;
  childName: string;
  fromName: string;
  toName: string;
  amountCents: number;
  note: string;
  requestedAt: string;
};

export type ChoreInstance = {
  id: string;
  name: string;
  icon: string;
  status: string;
  weight: number;
  isRequired: boolean;
  dueLocalDate: string;
  canClaim: boolean;
  /** May the CHILD take this back? True while a tick is still theirs --
   *  waiting on review, or self-certified and unconfirmed. */
  canUndo: boolean;
  /** A grown-up has answered this one. The child's side is closed. */
  settled: boolean;
  childId: string;
  childName: string;
};

export type Chart = {
  periodKey: string;
  startsAt: string;
  endsAt: string;
  payoutAt: string;
  instances: ChoreInstance[];
  today: ChoreInstance[];
  todayDone: number;
  todayTotal: number;
  days: string[];
  todayLocalDate: string;
  earnedBp: number;
  thresholdBp: number;
  policy: string;
  meetsThreshold: boolean;
  jobsStillNeeded: number;
  amountAtStakeCents: number;
  projectedPayoutCents: number;
  /** Finished weeks a grown-up still has to answer for. To a child these are
   *  not history -- they are money that has not turned up yet. */
  pendingReview: PendingReview[];
  /** Weeks that PAID and are waiting to be handed over. The jars hold these
   *  back until the child taps to collect, so the money arrives by being
   *  given rather than by having appeared while nobody was looking. */
  toCollect: Collectable[];
};

export type PendingReview = {
  periodKey: string;
  periodLabel: string;
  amountCents: number;
  earnedBp: number | null;
};

export type Collectable = {
  periodId: string;
  periodKey: string;
  periodLabel: string;
  amountCents: number;
  /** Each jar's share, read back from the payout's own postings -- the same
   *  pennies the ledger moved, not a split recomputed for display. */
  parts: CollectPart[];
};

export type CollectPart = {
  bucketId: string;
  key: string;
  name: string;
  kind: string;
  color: string | null;
  amountCents: number;
};

export type HistoryRow = {
  id: string;
  kind: string;
  label: string;
  memo: string;
  occurredAt: string;
  localDate: string | null;
  amountCents: number;
  deltaCents: number;
  periodKey: string | null;
  isReversed: boolean;
  isReversal: boolean;
  splitSnapshot: { key: string; amountCents: number }[];
  spendCategory: string | null;
};

export type Goal = {
  id: string;
  name: string;
  icon: string;
  targetCents: number;
  savedCents: number;
  remainingCents: number;
  progressBp: number;
  status: string;
  bucketKey: string;
};

export type CashOutRequest = {
  id: string;
  childId: string;
  childName: string;
  bucketName: string;
  bucketKey: string;
  amountCents: number;
  status: string;
  note: string;
  requestedAt: string;
};

export type ReviewQueue = {
  chores: ChoreInstance[];
  periods: {
    id: string;
    childId: string;
    childName: string;
    periodKey: string;
    /** The span of days, said the way a person says it: "Aug 1-7". The key is
     *  the period's START date, so showing it raw reads as the wrong day. */
    periodLabel: string;
    computedAmountCents: number;
    baseAmountCents: number;
    earnedBp: number | null;
    isCatchup: boolean;
    /** Why it is being asked about. "no_records" is a week the app was not
     *  running for; "chores_incomplete" is a week whose chart was kept and
     *  fell short. Two different questions, so two different sets of words. */
    reviewReason: "no_records" | "chores_incomplete";
    choresDone: number | null;
    choresRequired: number | null;
    choresMissed: number | null;
    choresExcused: number | null;
  }[];
  total: number;
};

/** The three answers to a week that fell short of its jobs. */
export type PeriodDecision = "full" | "partial" | "none";
